// Shared resolver for the Cancellation and Non-Renewal reason dropdowns (issue #33591).
//
// The `cancelReason` lookup is the SUPERSET of every cancellation / non-renewal reason. Which
// reasons a brand shows is declared per-brand in staff app config (`appConfig.cancelNonRenewalReasons`,
// an ordered list of reason ids):
//   - Brand WITH an enabled list (mutuals — Forreston/CFM/Oakwood): show ONLY those ids, in the
//     brand-declared order. The standardized reasons are tagged `optIn: true`.
//   - Brand WITHOUT an enabled list (Branch/BIX): legacy behavior — every non-deprecated reason
//     that is NOT `optIn` (so the mutual-only reasons never leak into the default dropdowns).
//
// Which dropdown a reason belongs in is driven by its `appliesTo` ('cancel' | 'nonRenew' | 'both').
// Legacy reasons predate that field and are classified by the older `nonRenew` boolean instead.
//
// This resolver only handles superset + applies-to + deprecated filtering. Callers still layer on
// their own state/permission gating (cancellation-form) and OH/PA first-term limits (non-renew-form).

const appliesToKind = (reason, kind) => {
  if (reason.appliesTo) {
    return reason.appliesTo === 'both' || reason.appliesTo === kind;
  }
  // Legacy reasons: the `nonRenew` boolean is the only signal.
  return kind === 'nonRenew' ? Boolean(reason.nonRenew) : !reason.nonRenew;
};

// A brand `cancelNonRenewalReasons` id that isn't in the superset is silently dropped (a typo, or a
// reason removed from the lookup). The per-brand tests catch this for the shipped configs, but warn
// (once per id, dev only) so a future hand-edited brand list surfaces the mistake instead of just
// showing a shorter dropdown. Deduped so a re-render can't spam the console.
const warnedUnknownReasonIds = new Set();
const warnUnknownReasonId = (id) => {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return;
  }
  if (warnedUnknownReasonIds.has(id)) {
    return;
  }
  warnedUnknownReasonIds.add(id);
  console.warn(
    `[cancel-nonrenew-reasons] brand enabled reason id "${id}" is not in the cancelReason superset; ignoring it.`
  );
};

/**
 * @param {object} params
 * @param {Array<{id: string, value: string, appliesTo?: string, optIn?: boolean, nonRenew?: boolean, deprecated?: boolean}>} params.allReasons
 *   The full reason superset (e.g. `lookupsJson.cancelReason`).
 * @param {string[]} [params.enabledReasonCodes] The brand's enabled reason ids, in display order.
 *   When absent/empty, falls back to legacy Branch behavior.
 * @param {'cancel' | 'nonRenew'} params.kind Which dropdown is being built.
 * @returns {Array} The reason options for this brand + dropdown, in the correct display order.
 */
export const getReasonOptions = ({ allReasons, enabledReasonCodes, kind }) => {
  if (enabledReasonCodes?.length) {
    const byId = new Map(allReasons.map((reason) => [reason.id, reason]));
    return enabledReasonCodes.reduce((options, id) => {
      const reason = byId.get(id);
      if (!reason) {
        warnUnknownReasonId(id);
        return options;
      }
      if (!reason.deprecated && appliesToKind(reason, kind)) {
        options.push(reason);
      }
      return options;
    }, []);
  }

  return allReasons.filter((reason) => !reason.deprecated && !reason.optIn && appliesToKind(reason, kind));
};
